/**
 * Animated Price Ticker Components
 * Features:
 * - WebSocket real-time price updates
 * - Scrolling marquee ticker bar
 * - Number flip animations on price changes
 * - Pulse and glow effects
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus, Sparkles, Wifi, WifiOff } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../../utils';

const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') + '/api';

// ============================================
// WEBSOCKET PRICE HOOK
// Shared hook for real-time price updates with polling fallback
// ============================================
const usePriceWebSocket = () => {
  const [prices, setPrices] = useState(null);
  const [prevPrices, setPrevPrices] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const wsFailedRef = useRef(false);

  // Fetch prices via HTTP (fallback)
  const fetchPrices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/metal-prices`);
      setPrevPrices(prev => prices || prev);
      setPrices(res.data);
    } catch (err) {
      console.error('Failed to fetch prices:', err);
    }
  }, [prices]);

  // Start polling fallback
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;
    console.log('Starting price polling fallback (every 10s)');
    pollingIntervalRef.current = setInterval(fetchPrices, 10000);
  }, [fetchPrices]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    // Skip WebSocket if it has failed before (use polling only)
    if (wsFailedRef.current) {
      startPolling();
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      wsRef.current = new WebSocket(`${WS_URL}/ws/prices`);

      wsRef.current.onopen = () => {
        setConnected(true);
        stopPolling();
        console.log('Price WebSocket connected');
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'price_update' && message.data) {
            setPrevPrices(prices);
            setPrices(message.data);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      wsRef.current.onclose = () => {
        setConnected(false);
        console.log('Price WebSocket disconnected');
        // After 3 failed attempts, switch to polling permanently
        wsFailedRef.current = true;
        startPolling();
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error, falling back to polling');
        wsFailedRef.current = true;
        wsRef.current?.close();
        startPolling();
      };

      // Timeout - if not connected in 5 seconds, switch to polling
      setTimeout(() => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection timeout, using polling');
          wsFailedRef.current = true;
          wsRef.current?.close();
          startPolling();
        }
      }, 5000);

    } catch (e) {
      console.error('WebSocket connection error:', e);
      wsFailedRef.current = true;
      startPolling();
    }
  }, [prices, startPolling, stopPolling]);

  // Fetch initial prices via HTTP, then try WebSocket
  useEffect(() => {
    fetchPrices(); // Initial fetch
    connect();     // Try WebSocket

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      stopPolling();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return { prices, prevPrices, connected };
};

// ============================================
// ANIMATED NUMBER COMPONENT
// Flip animation when number changes
// ============================================
const AnimatedNumber = ({ value, trend, decimals = 2 }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      setIsAnimating(true);
      
      // Animate the number change
      const startValue = prevValueRef.current || 0;
      const endValue = value;
      const duration = 800;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out cubic)
        const eased = 1 - Math.pow(1 - progress, 3);
        
        const current = startValue + (endValue - startValue) * eased;
        setDisplayValue(current);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(endValue);
          setTimeout(() => setIsAnimating(false), 200);
        }
      };

      requestAnimationFrame(animate);
      prevValueRef.current = value;
    }
  }, [value]);

  return (
    <span 
      className={`font-mono font-bold transition-all duration-300 ${
        isAnimating 
          ? trend === 'up' 
            ? 'text-green-400 scale-110' 
            : trend === 'down' 
              ? 'text-red-400 scale-110' 
              : 'text-white'
          : 'text-white'
      }`}
    >
      ${displayValue?.toFixed(decimals)}
    </span>
  );
};

// ============================================
// COMPACT PRICE TICKER (Navbar)
// Small ticker for navigation bar - Uses WebSocket
// ============================================
export const CompactPriceTicker = ({ className = '' }) => {
  const { prices, prevPrices, connected } = usePriceWebSocket();
  const [flash, setFlash] = useState({ gold: null, silver: null });
  const prevPricesRef = useRef(null);

  // Detect price changes and trigger flash
  useEffect(() => {
    if (prices && prevPricesRef.current) {
      if (prices.gold_blkback_price !== prevPricesRef.current.gold_blkback_price) {
        setFlash(prev => ({ 
          ...prev, 
          gold: prices.gold_blkback_price > prevPricesRef.current.gold_blkback_price ? 'up' : 'down' 
        }));
        setTimeout(() => setFlash(prev => ({ ...prev, gold: null })), 1500);
      }
      if (prices.silver_blkback_price !== prevPricesRef.current.silver_blkback_price) {
        setFlash(prev => ({ 
          ...prev, 
          silver: prices.silver_blkback_price > prevPricesRef.current.silver_blkback_price ? 'up' : 'down' 
        }));
        setTimeout(() => setFlash(prev => ({ ...prev, silver: null })), 1500);
      }
    }
    prevPricesRef.current = prices;
  }, [prices]);

  const getTrend = (current, previous, key) => {
    if (!previous || !current) return 'neutral';
    return current[key] > previous[key] ? 'up' : current[key] < previous[key] ? 'down' : 'neutral';
  };

  if (!prices) {
    return (
      <div className={`flex items-center gap-3 ${className}`} data-testid="price-ticker">
        <div className="animate-pulse bg-gray-800/50 rounded-md h-7 w-20"></div>
        <div className="animate-pulse bg-gray-800/50 rounded-md h-7 w-20"></div>
      </div>
    );
  }

  const goldTrend = getTrend(prices, prevPrices, 'gold_blkback_price');
  const silverTrend = getTrend(prices, prevPrices, 'silver_blkback_price');

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="price-ticker">
      {/* Gold */}
      <div 
        className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-500 overflow-hidden ${
          flash.gold === 'up' 
            ? 'bg-green-500/30 shadow-[0_0_15px_rgba(34,197,94,0.5)]' 
            : flash.gold === 'down' 
              ? 'bg-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.5)]' 
              : 'bg-orange-500/10 hover:bg-orange-500/20'
        }`}
      >
        {/* Shimmer effect */}
        {flash.gold && (
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        )}
        <span className="text-amber-300 font-bold text-[10px] tracking-wide">Au</span>
        <AnimatedNumber value={prices.gold_blkback_price} trend={goldTrend} />
        <div className={`transition-all duration-300 ${flash.gold ? 'scale-125' : ''}`}>
          {goldTrend === 'up' && <TrendingUp size={12} className="text-green-400" />}
          {goldTrend === 'down' && <TrendingDown size={12} className="text-red-400" />}
          {goldTrend === 'neutral' && <Minus size={12} className="text-gray-500" />}
        </div>
      </div>

      {/* Silver */}
      <div 
        className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-500 overflow-hidden ${
          flash.silver === 'up' 
            ? 'bg-green-500/30 shadow-[0_0_15px_rgba(34,197,94,0.5)]' 
            : flash.silver === 'down' 
              ? 'bg-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.5)]' 
              : 'bg-gray-500/10 hover:bg-gray-500/20'
        }`}
      >
        {flash.silver && (
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        )}
        <span className="text-gray-300 font-bold text-[10px] tracking-wide">Ag</span>
        <AnimatedNumber value={prices.silver_blkback_price} trend={silverTrend} />
        <div className={`transition-all duration-300 ${flash.silver ? 'scale-125' : ''}`}>
          {silverTrend === 'up' && <TrendingUp size={12} className="text-green-400" />}
          {silverTrend === 'down' && <TrendingDown size={12} className="text-red-400" />}
          {silverTrend === 'neutral' && <Minus size={12} className="text-gray-500" />}
        </div>
      </div>

      {/* Connection status indicator */}
      <div className="hidden sm:flex items-center gap-1">
        {connected ? (
          <>
            <Wifi size={10} className="text-green-500" />
            <span className="text-green-500 text-[9px] uppercase tracking-wider">Live</span>
          </>
        ) : (
          <>
            <WifiOff size={10} className="text-amber-400" />
            <span className="text-amber-400 text-[9px] uppercase tracking-wider">Est</span>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// SCROLLING MARQUEE TICKER
// Full-width scrolling ticker bar - Uses WebSocket
// ============================================
export const MarqueePriceTicker = ({ className = '' }) => {
  const { prices, prevPrices, connected } = usePriceWebSocket();

  if (!prices) return null;

  const goldTrend = prevPrices && prices.gold_blkback_price > prevPrices.gold_blkback_price ? 'up' 
    : prevPrices && prices.gold_blkback_price < prevPrices.gold_blkback_price ? 'down' : 'neutral';
  const silverTrend = prevPrices && prices.silver_blkback_price > prevPrices.silver_blkback_price ? 'up'
    : prevPrices && prices.silver_blkback_price < prevPrices.silver_blkback_price ? 'down' : 'neutral';

  const tickerItems = [
    { 
      label: 'GOLD BLKPack', 
      value: `$${prices.gold_blkback_price?.toFixed(2)}`,
      trend: goldTrend,
      color: 'text-amber-300',
      icon: '🥇'
    },
    { 
      label: 'SILVER BLKPack', 
      value: `$${prices.silver_blkback_price?.toFixed(2)}`,
      trend: silverTrend,
      color: 'text-gray-300',
      icon: '🥈'
    },
    { 
      label: 'Gold Spot', 
      value: `$${prices.gold_oz_price?.toFixed(2)}/oz`,
      trend: 'neutral',
      color: 'text-amber-400/70',
      icon: '📈'
    },
    { 
      label: 'Silver Spot', 
      value: `$${prices.silver_oz_price?.toFixed(2)}/oz`,
      trend: 'neutral',
      color: 'text-gray-400',
      icon: '📊'
    },
    { 
      label: 'Gold Line', 
      value: 'Silver Line Collectibles',
      trend: 'neutral',
      color: 'text-amber-400',
      icon: '💎'
    },
    {
      label: connected ? 'LIVE' : 'CONNECTING',
      value: connected ? '● Real-time' : '○ Reconnecting...',
      trend: 'neutral',
      color: connected ? 'text-green-400' : 'text-amber-300',
      icon: connected ? '📡' : '🔄'
    }
  ];

  return (
    <div className={`relative overflow-hidden bg-gradient-to-r from-black via-gray-900/95 to-[#12121e] border-y border-orange-500/20 ${className}`}>
      {/* Gradient fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>
      
      <div className="ticker-container py-2.5">
        <div className="ticker-track flex gap-12 animate-marquee hover:animation-pause">
          {/* Triple the items for seamless loop */}
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-lg">{item.icon}</span>
              <span className={`text-sm font-medium ${item.color}`}>{item.label}:</span>
              <span className={`text-sm font-bold ${
                item.trend === 'up' ? 'text-green-400' : 
                item.trend === 'down' ? 'text-red-400' : 
                'text-white'
              }`}>
                {item.value}
              </span>
              {item.trend === 'up' && <TrendingUp size={14} className="text-green-400" />}
              {item.trend === 'down' && <TrendingDown size={14} className="text-red-400" />}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
        .ticker-container:hover .animate-marquee {
          animation-play-state: paused;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1s ease-in-out;
        }
      `}</style>
    </div>
  );
};

// ============================================
// LARGE PRICE DISPLAY CARD
// For homepage/hero sections
// ============================================
export const PriceDisplayCard = ({ metal = 'gold', className = '' }) => {
  const [prices, setPrices] = useState(null);
  const [prevPrices, setPrevPrices] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await axios.get(`${API}/metal-prices`);
        
        const key = metal === 'gold' ? 'gold_blkback_price' : 'silver_blkback_price';
        if (prices && res.data[key] !== prices[key]) {
          setIsFlashing(true);
          setTimeout(() => setIsFlashing(false), 1500);
        }
        
        setPrevPrices(prices);
        setPrices(res.data);
      } catch (err) {
        console.error('Failed to fetch prices:', err);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!prices) {
    return (
      <div className={`animate-pulse bg-gray-800/50 rounded-xl h-32 w-48 ${className}`}></div>
    );
  }

  const isGold = metal === 'gold';
  const price = isGold ? prices.gold_blkback_price : prices.silver_blkback_price;
  const spotPrice = isGold ? prices.gold_oz_price : prices.silver_oz_price;
  const key = isGold ? 'gold_blkback_price' : 'silver_blkback_price';
  const trend = prevPrices && prices[key] > prevPrices[key] ? 'up' 
    : prevPrices && prices[key] < prevPrices[key] ? 'down' : 'neutral';

  return (
    <div 
      className={`relative overflow-hidden rounded-xl p-4 transition-all duration-500 ${
        isGold 
          ? 'bg-gradient-to-br from-orange-900/30 to-orange-600/10 border border-orange-500/30' 
          : 'bg-gradient-to-br from-gray-800/50 to-gray-600/10 border border-gray-500/30'
      } ${
        isFlashing 
          ? trend === 'up' 
            ? 'shadow-[0_0_30px_rgba(34,197,94,0.5)] scale-[1.02]' 
            : trend === 'down' 
              ? 'shadow-[0_0_30px_rgba(239,68,68,0.5)] scale-[1.02]' 
              : ''
          : 'hover:shadow-lg'
      } ${className}`}
    >
      {/* Animated background on price change */}
      {isFlashing && (
        <div className={`absolute inset-0 animate-pulse ${
          trend === 'up' ? 'bg-green-500/10' : trend === 'down' ? 'bg-red-500/10' : ''
        }`}></div>
      )}

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm font-bold uppercase tracking-wider ${isGold ? 'text-amber-300' : 'text-gray-300'}`}>
            {isGold ? '🥇 Gold' : '🥈 Silver'} BLKPack
          </span>
          {trend === 'up' && <TrendingUp className="text-green-400" size={18} />}
          {trend === 'down' && <TrendingDown className="text-red-400" size={18} />}
        </div>
        
        <div className={`text-3xl font-black mb-1 transition-all duration-300 ${
          isFlashing 
            ? trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-white'
            : 'text-white'
        }`}>
          ${price?.toFixed(2)}
        </div>
        
        <div className="text-xs text-gray-500">
          Spot: ${spotPrice?.toFixed(2)}/oz
        </div>
        
        {/* Sparkle effect */}
        {isFlashing && (
          <Sparkles className="absolute top-2 right-2 text-amber-300 animate-spin" size={16} />
        )}
      </div>
    </div>
  );
};

// ============================================
// MINI INLINE TICKER
// For use within text or cards
// ============================================
export const InlinePriceTicker = ({ showBoth = true, size = 'sm' }) => {
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await axios.get(`${API}/metal-prices`);
        setPrices(res.data);
      } catch (err) {
        console.error('Failed to fetch prices:', err);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!prices) return <span className="animate-pulse bg-gray-700 rounded w-16 h-4 inline-block"></span>;

  const textSize = size === 'xs' ? 'text-xs' : size === 'sm' ? 'text-sm' : 'text-base';

  return (
    <span className={`inline-flex items-center gap-2 ${textSize}`}>
      <span className="text-amber-300 font-medium">
        Au ${prices.gold_blkback_price?.toFixed(2)}
      </span>
      {showBoth && (
        <>
          <span className="text-gray-600">|</span>
          <span className="text-gray-300 font-medium">
            Ag ${prices.silver_blkback_price?.toFixed(2)}
          </span>
        </>
      )}
    </span>
  );
};

// ============================================
// FOOTER PRICE BAR
// Sticky footer price display
// ============================================
export const FooterPriceBar = () => {
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await axios.get(`${API}/metal-prices`);
        setPrices(res.data);
      } catch (err) {
        console.error('Failed to fetch prices:', err);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!prices) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[#12121e]/95 backdrop-blur-md border-t border-orange-500/20 py-2 px-4 z-40">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-amber-300 font-bold text-xs">GOLD</span>
            <span className="text-white font-mono font-bold">${prices.gold_blkback_price?.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 font-bold text-xs">SILVER</span>
            <span className="text-white font-mono font-bold">${prices.silver_blkback_price?.toFixed(2)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-gray-500 text-xs">Live Prices</span>
        </div>
      </div>
    </div>
  );
};

export default CompactPriceTicker;
